import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import Splash from "../components/homepage/Splash"
import TwoColGrid from "../components/homepage/TwoColGrid"
import Timeline from "../components/homepage/Timeline"
import Team from "../components/homepage/Team"
import Supporters from "../components/homepage/Supporters"
import News from "../components/homepage/News"
import Contact from "../components/homepage/Contact"
import SEO from "../components/seo"
import DataSection from "../components/homepage/DataSection"

const IndexPage = ({ data }) => {
  const page = data.prismicHomepage.data

  const body = page.body.map((slice, index) => {
    const key = `slice-${index}`
    switch (slice.slice_type) {
      case "process":
        return (
          <TwoColGrid
            key={key}
            eyebrow="Enzyme Design Platform"
            title="Designing enzymes, redefining chemistry"
            lede="AI-powered enzyme discovery and design — building the next generation of biocatalysts for sustainable manufacturing."
            points={[
              "Precise control over selectivity unlocks synthesis routes conventional chemistry can't reach.",
              "Less energy, less waste, less reliance on fossil feedstocks.",
              "Direct pathways from renewable, upcycled feedstocks to everyday cosmetics, personal care, and food products.",
            ]}
          />
        )
      case "timeline":
        return <Timeline key={key} data={slice} />
      case "news":
        return <News key={key} data={slice} />
      case "team":
        return (
          <React.Fragment key={key}>
            <Team data={slice} />
            <Supporters />
          </React.Fragment>
        )
      case "contact":
        return <Contact key={key} data={slice.primary} />
      case "data":
        return (
          <DataSection
            key={key}
            eyebrow="Proprietary Data Meets AI"
            title="Proprietary datasets, validated in our lab"
            paragraph="Unique enzyme datasets power our ML models, capturing not only novel enzyme functions, substrates, and transformations, but also the physical realities of the chemistry and reaction conditions through rich metadata. This continuous data stream creates an active learning loop, enabling our models to uncover the true drivers of enzyme specificity and performance."
            videoUrl={slice.primary.data_video?.url}
            posterUrl={slice.primary.data_poster?.url}
          />
        )
      default:
        return null
    }
  })

  // Ensure numeric opacity
  const overlayOpacity = Number.isFinite(parseFloat(page.hero_overlay_opacity))
    ? parseFloat(page.hero_overlay_opacity)
    : 0.45

  return (
    <Layout>
      <SEO />
<Splash
  title={page.page_title?.text}
  kicker="Biocatalysis for the Future of Biomanufacturing"
  heroVideo={page.hero_video?.url}
  heroPoster={page.hero_poster?.url}
  overlayOpacity={overlayOpacity}
  align="left"
/>
      {body}
    </Layout>
  )
}

export const pageQuery = graphql`
  query IndexQuery {
    prismicHomepage {
      data {
        social {
          social_icon { alt url }
          social_icon_link { url }
        }
        body {
          ... on PrismicHomepageBodyContact {
            slice_type
            primary {
              contact_button_text
              contact_field_email
              contact_field_message
              contact_field_name
              contact_title { text }
              contact_background_image {
                alt
                url
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 2000) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
            }
          }
          ... on PrismicHomepageBodyNews {
            slice_type
            primary { news_title { text } }
            items {
              news_item_date
              news_item_link { url }
              news_item_title { text }
            }
          }
          ... on PrismicHomepageBodyProcess {
            slice_type
            primary {
              process_text { text }
              process_title { text }
            }
          }
          ... on PrismicHomepageBodyTeam {
            slice_type
            primary { team_title { text } }
            items {
              team_member_link { url link_type }
              team_member_name { text }
              team_member_portrait { url alt }
              team_member_position { text }
            }
          }
          ... on PrismicHomepageBodyTimeline {
            slice_type
            primary { timeline_title { text } }
            items {
              timeline_item_date
              timeline_item_text { text }
            }
          }
          ... on PrismicHomepageBodyData {
            slice_type
            primary {
              data_title { text }
              data_text { text }
              data_video { url }
              data_poster { url }
            }
          }
        }
        page_title { text }
        hero_video { url }
        hero_poster { url }
        hero_overlay_opacity
      }
    }
  }
`

export default IndexPage
